import { useEffect, useState, useCallback, useMemo, useRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../App";
import api from "../lib/api";
import { fmt, fmtDate, ESTADO_PRESUPUESTO_LABEL } from "../lib/utils";
import {
  Button, Input, Textarea, Select, FormField, Dialog,
  Badge, Spinner, Empty, PageHeader, SearchInput,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Card, CardContent,
} from "../components/ui";
import { Plus, Trash2, Printer, Pencil, ClipboardCheck, ClipboardList, ChevronsDown, X, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import ConformidadCliente from "../components/ConformidadCliente";

const ESTADO_BADGE = {
  BORRADOR:  "bg-secondary text-secondary-foreground border-border",
  ENVIADO:   "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300",
  APROBADO:  "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300",
  RECHAZADO: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300",
};

const BADGE_REDUCIDO = "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300";

const TIPOS_ITEM = ["MANO_OBRA", "REPUESTO", "PINTURA", "OTROS"];

function emptyItem() {
  return { descripcion: "", tipo: "MANO_OBRA", cantidad: 1, precio_unitario: 0, codigo: "" };
}
function emptyRepuesto() {
  return { descripcion: "", codigo: "", cantidad: 1, precio_unitario: 0 };
}

export default function Presupuestos() {
  const location = useLocation();
  const { setPresupuestosReducidos } = useContext(AuthContext);
  const [list,       setList]       = useState([]);
  const [recepciones,setRecep]       = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filtroChapa,       setFiltroChapa]       = useState("");
  const [filtroAseguradora, setFiltroAseguradora]  = useState("");
  const [filtroEstado,      setFiltroEstado]       = useState("");
  const [filtroMes,         setFiltroMes]          = useState("");
  const [fechaDesde,        setFechaDesde]         = useState("");
  const [fechaHasta,        setFechaHasta]         = useState("");
  const [open,       setOpen]       = useState(false);
  const [detail,     setDetail]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [conformidad, setConformidad] = useState(null);

  // Formulario
  const [recepcion_id, setRecepId] = useState("");
  const [recepData,    setRecepData] = useState(null);  // datos de la recepción elegida
  const [items,        setItems]    = useState([emptyItem()]);
  const [repuestos,    setRepuestos] = useState([]);
  const [descuento,    setDescuento] = useState(0);
  const [iva_pct,      setIva]       = useState(10);
  const [obs,          setObs]       = useState("");

  const load = useCallback(async () => {
    const [p, r2, a2] = await Promise.all([
      api.get("/presupuestos"),
      api.get("/recepciones"),
      api.get("/aseguradoras"),
    ]);
    setList(p.data);
    setRecep(r2.data.filter((x) => ["INGRESADO","EN_PRESUPUESTO"].includes(x.estado)));
    setAseguradoras(a2.data);
    setPresupuestosReducidos?.(p.data.filter((x) => x.aprobado_reducido).length);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = useCallback(() => {
    setRecepId(""); setRecepData(null);
    setItems([emptyItem()]); setRepuestos([]); setDescuento(0); setIva(10); setObs("");
  }, []);

  // Auto-abrir si viene desde Recepción con recepcion_id
  // Se dispara solo cuando loading cambia a false (primera carga)
  const autoOpenDone = useRef(false);
  useEffect(() => {
    if (loading || autoOpenDone.current) return;
    if (location.state?.recepcion_id) {
      autoOpenDone.current = true;
      const id = String(location.state.recepcion_id);
      resetForm();
      setRecepId(id);
      const rec = recepciones.find((x) => String(x.id) === id);
      setRecepData(rec ?? null);
      setOpen(true);
      window.history.replaceState({}, "");
    }
  }, [loading, location.state, recepciones, resetForm]);

  function addItem() { setItems((i) => [...i, emptyItem()]); }
  function removeItem(idx) { setItems((i) => i.filter((_, j) => j !== idx)); }
  function updateItem(idx, field, val) {
    setItems((prev) => prev.map((it, j) => {
      if (j !== idx) return it;
      const updated = { ...it, [field]: field === "cantidad" || field === "precio_unitario" ? parseFloat(val) || 0 : val };
      return updated;
    }));
  }
  function addRepuesto() { setRepuestos((r) => [...r, emptyRepuesto()]); }
  function removeRepuesto(idx) { setRepuestos((r) => r.filter((_, j) => j !== idx)); }
  function updateRepuesto(idx, field, val) {
    setRepuestos((prev) => prev.map((it, j) => {
      if (j !== idx) return it;
      return { ...it, [field]: field === "cantidad" || field === "precio_unitario" ? parseFloat(val) || 0 : val };
    }));
  }

  // Cálculos
  const subtotal = [...items, ...repuestos.map((r) => ({ ...r, tipo: "REPUESTO" }))]
    .reduce((s, i) => s + (i.cantidad * i.precio_unitario), 0);
  const descMonto = subtotal * descuento / 100;
  const base      = subtotal - descMonto;
  const ivaMonto  = base * iva_pct / 100;
  const total     = base + ivaMonto;

  async function selectRecepcion(id) {
    setRecepId(id);
    if (!id) { setRecepData(null); return; }
    const r = recepciones.find((x) => String(x.id) === String(id));
    setRecepData(r ?? null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!recepcion_id) { toast.error("Seleccioná una recepción"); return; }
    if (items.length === 0) { toast.error("Agregá al menos un ítem"); return; }
    setSaving(true);
    try {
      const rec = recepciones.find((x) => String(x.id) === String(recepcion_id));
      if (!rec) { toast.error("Recepción no encontrada"); setSaving(false); return; }
      await api.post("/presupuestos", {
        recepcion_id: parseInt(recepcion_id),
        cliente_id:   rec.cliente_id,
        vehiculo_id:  rec.vehiculo_id,
        descuento_pct: descuento,
        iva_pct,
        observaciones: obs || null,
        items: [
          ...items.map((i) => ({
            descripcion: i.descripcion,
            tipo: i.tipo,
            cantidad: i.cantidad,
            precio_unitario: i.precio_unitario,
            codigo: i.codigo || null,
          })),
          ...repuestos.map((r) => ({
            descripcion: r.descripcion,
            tipo: "REPUESTO",
            cantidad: r.cantidad,
            precio_unitario: r.precio_unitario,
            codigo: r.codigo || null,
          })),
        ],
      });
      toast.success("Presupuesto guardado");
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function cambiarEstado(id, estado) {
    try {
      await api.patch(`/presupuestos/${id}/estado`, null, { params: { estado } });
      toast.success(`Estado → ${estado}`);
      load();
      if (detail?.id === id) setDetail((d) => ({ ...d, estado }));
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al cambiar estado");
    }
  }

  async function imprimirConformidad(id) {
    try {
      const r = await api.get(`/presupuestos/${id}/conformidad`);
      setConformidad({ data: r.data, autoPrint: false });
    } catch {
      toast.error("No se pudo cargar la conformidad");
    }
  }

  function ymd(d) {
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  function handleMesChange(val) {
    setFiltroMes(val);
    if (!val) return;
    const [y, m] = val.split("-").map(Number);
    setFechaDesde(ymd(new Date(y, m - 1, 1)));
    setFechaHasta(ymd(new Date(y, m, 0)));
  }

  const mesesOptions = useMemo(() => Array.from({ length: 24 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("es-PY", { month: "long", year: "numeric" });
    return { value, label: label.charAt(0).toUpperCase() + label.slice(1) };
  }), []);

  function clearFilters() {
    setSearch(""); setFiltroChapa(""); setFiltroAseguradora(""); setFiltroEstado("");
    setFiltroMes(""); setFechaDesde(""); setFechaHasta("");
  }

  const hasActiveFilters = !!(search || filtroChapa || filtroAseguradora || filtroEstado || fechaDesde || fechaHasta);

  const filtered = list.filter((p) => {
    if (search) {
      const q = search.toLowerCase();
      if (!(p.numero?.toLowerCase().includes(q) || p.cliente_nombre?.toLowerCase().includes(q))) return false;
    }
    if (filtroChapa) {
      const chapa = (p.vehiculo_patente ?? "").toLowerCase();
      if (!chapa.includes(filtroChapa.toLowerCase())) return false;
    }
    if (filtroAseguradora) {
      if (filtroAseguradora === "_PARTICULAR_") {
        if (p.aseguradora_id) return false;
      } else if (String(p.aseguradora_id ?? "") !== filtroAseguradora) {
        return false;
      }
    }
    if (filtroEstado && p.estado !== filtroEstado) return false;
    if (fechaDesde && (!p.fecha || ymd(p.fecha) < fechaDesde)) return false;
    if (fechaHasta && (!p.fecha || ymd(p.fecha) > fechaHasta)) return false;
    return true;
  });

  function exportExcel() {
    if (filtered.length === 0) { toast.error("No hay presupuestos para exportar"); return; }
    const headers = ["N°", "Fecha", "Cliente", "Vehículo", "Chapa", "Aseguradora", "Total", "Estado"];
    const dataRows = filtered.map((p) => [
      p.numero,
      fmtDate(p.fecha),
      p.cliente_nombre,
      p.vehiculo_desc,
      p.vehiculo_patente ?? "",
      p.aseguradora_nombre ?? "Particular",
      Math.round(p.total ?? 0),
      ESTADO_PRESUPUESTO_LABEL[p.estado] ?? p.estado,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    ws["!cols"] = [
      { wch: 10 }, { wch: 11 }, { wch: 24 }, { wch: 28 },
      { wch: 12 }, { wch: 20 }, { wch: 14 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Presupuestos");
    XLSX.writeFile(wb, `presupuestos_${ymd(new Date())}.xlsx`);
  }

  function exportPdf() {
    if (filtered.length === 0) { toast.error("No hay presupuestos para exportar"); return; }
    const totalGeneral = filtered.reduce((s, p) => s + (p.total ?? 0), 0);
    const rowsHtml = filtered.map((p) => `
      <tr>
        <td>${p.numero ?? ""}</td>
        <td>${fmtDate(p.fecha)}</td>
        <td>${p.cliente_nombre ?? ""}</td>
        <td>${p.vehiculo_desc ?? ""}</td>
        <td>${p.vehiculo_patente ?? ""}</td>
        <td>${p.aseguradora_nombre ?? "Particular"}</td>
        <td class="tr">${fmt(p.total)}</td>
        <td>${ESTADO_PRESUPUESTO_LABEL[p.estado] ?? p.estado}</td>
      </tr>`).join("");

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8"/>
<title>Presupuestos</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #000; padding: 18px 22px; }
  h1 { font-size: 16px; margin-bottom: 4px; }
  .sub { font-size: 10px; color: #555; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; }
  th { font-size: 10px; text-align: left; padding: 4px 6px; background: #f0f0f0; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; }
  td { font-size: 10.5px; padding: 3px 6px; border-bottom: 1px solid #e5e5e5; }
  .tr { text-align: right; }
  .total-row td { font-weight: bold; border-top: 1.5px solid #000; }
  @media print { body { padding: 10px; } }
</style>
</head>
<body>
  <h1>Listado de Presupuestos</h1>
  <div class="sub">${filtered.length} presupuestos · Generado el ${fmtDate(new Date())}</div>
  <table>
    <thead>
      <tr><th>N°</th><th>Fecha</th><th>Cliente</th><th>Vehículo</th><th>Chapa</th><th>Aseguradora</th><th class="tr">Total</th><th>Estado</th></tr>
    </thead>
    <tbody>
      ${rowsHtml}
      <tr class="total-row"><td colspan="6"></td><td class="tr">${fmt(totalGeneral)}</td><td></td></tr>
    </tbody>
  </table>
</body>
</html>`;

    const w = window.open("", "_blank", "width=1000,height=700");
    if (!w) { toast.error("Popup bloqueado. Permitir popups e intentar de nuevo."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  }

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Presupuestos" sub={`${filtered.length} de ${list.length} presupuestos`}>
        <Button onClick={() => { resetForm(); setOpen(true); }}>
          <Plus className="w-4 h-4" /> Nuevo presupuesto
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por número o cliente…" className="w-full sm:w-56" />
        <FormField label="Chapa" className="w-full sm:w-32">
          <Input value={filtroChapa} onChange={(e) => setFiltroChapa(e.target.value)} placeholder="AAHX 209" />
        </FormField>
        <FormField label="Aseguradora" className="w-full sm:w-44">
          <Select value={filtroAseguradora} onChange={(e) => setFiltroAseguradora(e.target.value)}>
            <option value="">Todas</option>
            <option value="_PARTICULAR_">Particular</option>
            {aseguradoras.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </Select>
        </FormField>
        <FormField label="Estado" className="w-full sm:w-36">
          <Select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Todos</option>
            {Object.keys(ESTADO_BADGE).map((e) => (
              <option key={e} value={e}>{ESTADO_PRESUPUESTO_LABEL[e] ?? e}</option>
            ))}
          </Select>
        </FormField>
        <FormField label="Mes" className="w-full sm:w-44">
          <Select value={filtroMes} onChange={(e) => handleMesChange(e.target.value)}>
            <option value="">Todos los meses</option>
            {mesesOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
        </FormField>
        <FormField label="Desde" className="w-full sm:w-40">
          <Input type="date" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setFiltroMes(""); }} />
        </FormField>
        <FormField label="Hasta" className="w-full sm:w-40">
          <Input type="date" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setFiltroMes(""); }} />
        </FormField>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar filtros</Button>
        )}
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={exportExcel}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={exportPdf}>
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>N°</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Vehículo</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={7}><Empty message="No hay presupuestos" /></TableCell></TableRow>
          )}
          {filtered.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-mono text-xs">{p.numero}</TableCell>
              <TableCell className="text-sm">{fmtDate(p.fecha)}</TableCell>
              <TableCell className="text-sm">{p.cliente_nombre}</TableCell>
              <TableCell className="text-sm">{p.vehiculo_desc}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(p.total)}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <Badge className={ESTADO_BADGE[p.estado] ?? "bg-secondary"}>
                    {p.estado}
                  </Badge>
                  {p.aprobado_reducido && (
                    <Badge className={BADGE_REDUCIDO}>
                      REDUCIDO
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <button
                  onClick={() => setDetail(p)}
                  className="text-xs text-primary hover:underline"
                >
                  Ver
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog: nuevo presupuesto en pantalla completa dividida */}
      <Dialog open={open} onClose={() => setOpen(false)} title="Nuevo Presupuesto" size="2xl" className="!max-h-[96vh]">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Panel izquierdo 65% */}
            <div className="flex-1 min-w-0 space-y-4">
              <FormField label="Recepción / trabajo">
                <Select value={recepcion_id} onChange={(e) => selectRecepcion(e.target.value)} required>
                  <option value="">Seleccionar recepción…</option>
                  {recepciones.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.numero} — {r.vehiculo_desc} ({r.cliente_nombre})
                    </option>
                  ))}
                </Select>
              </FormField>

              {recepData && (
                <div className="text-xs bg-secondary rounded-md p-3 text-muted-foreground">
                  <span className="font-medium text-foreground">{recepData.vehiculo_desc}</span>
                  {recepData.descripcion_danio && ` — ${recepData.descripcion_danio}`}
                </div>
              )}

              {/* Tabla de ítems */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Ítems</span>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-3.5 h-3.5" /> Agregar ítem
                  </Button>
                </div>
                <div className="overflow-x-auto">
                <div className="space-y-2 min-w-[480px]">
                  {items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-end">
                      <FormField label={idx === 0 ? "Descripción" : undefined}>
                        <div className="flex gap-1">
                          {item.tipo === "REPUESTO" && (
                            <Input
                              value={item.codigo}
                              onChange={(e) => updateItem(idx, "codigo", e.target.value)}
                              placeholder="Código"
                              className="w-24"
                            />
                          )}
                          <Input
                            value={item.descripcion}
                            onChange={(e) => updateItem(idx, "descripcion", e.target.value)}
                            placeholder="Descripción del trabajo / repuesto…"
                            required
                          />
                        </div>
                      </FormField>
                      <FormField label={idx === 0 ? "Tipo" : undefined}>
                        <Select value={item.tipo} onChange={(e) => updateItem(idx, "tipo", e.target.value)} className="w-32">
                          {TIPOS_ITEM.map((t) => <option key={t}>{t}</option>)}
                        </Select>
                      </FormField>
                      <FormField label={idx === 0 ? "Cant." : undefined}>
                        <Input
                          type="number" min="0.01" step="0.01" className="w-20"
                          value={item.cantidad}
                          onChange={(e) => updateItem(idx, "cantidad", e.target.value)}
                        />
                      </FormField>
                      <FormField label={idx === 0 ? "P. Unit." : undefined}>
                        <Input
                          type="number" min="0" step="100" className="w-28"
                          value={item.precio_unitario}
                          onChange={(e) => updateItem(idx, "precio_unitario", e.target.value)}
                          onFocus={(e) => e.target.select()}
                        />
                      </FormField>
                      <div className={idx === 0 ? "pb-0 mt-5" : ""}>
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          disabled={items.length === 1}
                          className="p-2 text-muted-foreground hover:text-destructive disabled:opacity-30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </div>

              {/* Tabla de repuestos */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Repuestos</span>
                  <Button type="button" variant="outline" size="sm" onClick={addRepuesto}>
                    <Plus className="w-3.5 h-3.5" /> Agregar repuesto
                  </Button>
                </div>
                {repuestos.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sin repuestos. Presioná "Agregar repuesto" para añadir.</p>
                ) : (
                  <div className="overflow-x-auto">
                  <div className="space-y-2 min-w-[460px]">
                    {repuestos.map((rep, idx) => (
                      <div key={idx} className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-2 items-end">
                        <FormField label={idx === 0 ? "Código" : undefined}>
                          <Input
                            value={rep.codigo}
                            onChange={(e) => updateRepuesto(idx, "codigo", e.target.value)}
                            placeholder="Cód."
                            className="w-20"
                          />
                        </FormField>
                        <FormField label={idx === 0 ? "Descripción" : undefined}>
                          <Input
                            value={rep.descripcion}
                            onChange={(e) => updateRepuesto(idx, "descripcion", e.target.value)}
                            placeholder="Nombre del repuesto…"
                            required
                          />
                        </FormField>
                        <FormField label={idx === 0 ? "Cant." : undefined}>
                          <Input
                            type="number" min="0.01" step="0.01" className="w-20"
                            value={rep.cantidad}
                            onChange={(e) => updateRepuesto(idx, "cantidad", e.target.value)}
                          />
                        </FormField>
                        <FormField label={idx === 0 ? "P. Unit." : undefined}>
                          <Input
                            type="number" min="0" step="100" className="w-28"
                            value={rep.precio_unitario}
                            onChange={(e) => updateRepuesto(idx, "precio_unitario", e.target.value)}
                            onFocus={(e) => e.target.select()}
                          />
                        </FormField>
                        <div className={idx === 0 ? "pb-0 mt-5" : ""}>
                          <button
                            type="button"
                            onClick={() => removeRepuesto(idx)}
                            className="p-2 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                )}
              </div>

              <FormField label="Observaciones">
                <Textarea rows={2} value={obs} onChange={(e) => setObs(e.target.value)} />
              </FormField>
            </div>

            {/* Panel derecho resumen */}
            <div className="w-full lg:w-64 shrink-0">
              <Card className="sticky top-0">
                <CardContent className="p-4 space-y-3">
                  <p className="font-semibold text-sm font-display">Resumen</p>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-mono">{fmt(subtotal)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs w-20">Descuento %</span>
                      <Input
                        type="number" min="0" max="100" step="0.5"
                        value={descuento}
                        onChange={(e) => setDescuento(parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs w-16"
                      />
                      <span className="font-mono ml-auto">-{fmt(descMonto)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs w-20">IVA %</span>
                      <Input
                        type="number" min="0" max="100"
                        value={iva_pct}
                        onChange={(e) => setIva(parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs w-16"
                      />
                      <span className="font-mono ml-auto">{fmt(ivaMonto)}</span>
                    </div>
                  </div>

                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between items-baseline">
                      <span className="font-semibold">TOTAL</span>
                      <span className="font-mono font-bold text-xl text-primary">{fmt(total)}</span>
                    </div>
                  </div>

                  <Button type="submit" loading={saving} className="w-full mt-2">
                    Guardar presupuesto
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Dialog>

      {/* Detalle presupuesto */}
      {detail && (
        <PreDetail
          pre={detail}
          onClose={() => setDetail(null)}
          onEstado={cambiarEstado}
          onReload={load}
          onImprimirConformidad={imprimirConformidad}
        />
      )}

      {/* Conformidad del cliente */}
      {conformidad && (
        <ConformidadCliente
          data={conformidad.data}
          autoPrint={conformidad.autoPrint}
          onClose={() => setConformidad(null)}
        />
      )}
    </div>
  );
}

function PreDetail({ pre, onClose, onEstado, onReload, onImprimirConformidad }) {
  const [full, setFull] = useState(pre);
  const [editRep, setEditRep] = useState(null);   // presupuesto_repuesto being edited
  const [savingRep, setSavingRep] = useState(false);
  const [repForm, setRepForm] = useState({ proveedor_id: "", fecha_estimada_llegada: "", notas: "" });
  const [proveedores, setProveedores] = useState([]);
  const [bajaDialog, setBajaDialog] = useState(null); // id del item en dar de baja
  const [responsable, setResponsable] = useState("");
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [fechaPago,      setFechaPago]      = useState("");
  const [savingApproval, setSavingApproval] = useState(false);

  // Estado del formulario de edición del presupuesto
  const [editOpen,    setEditOpen]    = useState(false);
  const [editItems,   setEditItems]   = useState([]);
  const [editDesc,    setEditDesc]    = useState(0);
  const [editIva,     setEditIva]     = useState(10);
  const [editObs,     setEditObs]     = useState("");
  const [editFecha,   setEditFecha]   = useState("");
  const [savingEdit,  setSavingEdit]  = useState(false);
  const [editTotalAprobadoMode, setEditTotalAprobadoMode] = useState(false);
  const [editTotalAprobado,     setEditTotalAprobado]     = useState(0);

  // Estado del formulario de edición de aseguradora
  const [editAsegOpen, setEditAsegOpen] = useState(false);
  const [editAsegForm, setEditAsegForm] = useState({ aseguradora_id: "", numero_poliza: "", numero_siniestro: "", agente: "", numero_provision: "" });
  const [aseguradoras, setAseguradoras] = useState([]);
  const [savingAseg,   setSavingAseg]   = useState(false);

  const reload = useCallback(() => {
    api.get(`/presupuestos/${pre.id}`).then((r) => setFull(r.data));
  }, [pre.id]);

  useEffect(() => {
    reload();
    api.get("/proveedores").then((r) => setProveedores(r.data));
    api.get("/aseguradoras").then((r) => setAseguradoras(r.data));
  }, [reload]);

  function openEditPresupuesto() {
    const allItems = (full.items ?? []).map((it) => ({
      descripcion: it.descripcion,
      tipo: it.tipo,
      cantidad: it.cantidad,
      precio_unitario: it.precio_unitario,
      codigo: it.repuesto_codigo ?? "",
    }));
    setEditItems(allItems.length > 0 ? allItems : [emptyItem()]);
    setEditDesc(full.descuento_pct ?? 0);
    setEditIva(full.iva_pct ?? 10);
    setEditObs(full.observaciones ?? "");
    const fechaISO = full.fecha ? new Date(full.fecha).toISOString().slice(0, 10) : "";
    setEditFecha(fechaISO);
    setEditTotalAprobadoMode(full.aprobado_reducido ?? false);
    setEditTotalAprobado(full.total_aprobado > 0 ? full.total_aprobado : 0);
    setEditOpen(true);
  }

  function addEditItem() { setEditItems((i) => [...i, emptyItem()]); }
  function removeEditItem(idx) { setEditItems((i) => i.filter((_, j) => j !== idx)); }
  function updateEditItem(idx, field, val) {
    setEditItems((prev) => prev.map((it, j) => {
      if (j !== idx) return it;
      return { ...it, [field]: field === "cantidad" || field === "precio_unitario" ? parseFloat(val) || 0 : val };
    }));
  }

  const editSubtotal  = editItems.reduce((s, i) => s + i.cantidad * i.precio_unitario, 0);
  const editDescMonto = editSubtotal * editDesc / 100;
  const editBase      = editSubtotal - editDescMonto;
  const editIvaMonto  = editBase * editIva / 100;
  const editTotal     = editBase + editIvaMonto;

  // Reducción aprobada: recalcular IVA desde el total reducido
  const reducedTotalNum = parseFloat(editTotalAprobado) || 0;
  const reducedIvaMonto = editTotalAprobadoMode && reducedTotalNum > 0
    ? reducedTotalNum * editIva / (100 + editIva)
    : 0;

  async function handleSaveEdit(e) {
    e.preventDefault();
    if (editItems.length === 0) { toast.error("Agregá al menos un ítem"); return; }
    setSavingEdit(true);
    try {
      const updated = await api.put(`/presupuestos/${full.id}`, {
        descuento_pct: editDesc,
        iva_pct: editIva,
        observaciones: editObs || null,
        fecha_emision: editFecha ? editFecha + "T12:00:00" : null,
        items: editItems.map((i) => ({
          descripcion: i.descripcion,
          tipo: i.tipo,
          cantidad: i.cantidad,
          precio_unitario: i.precio_unitario,
          codigo: i.codigo || null,
        })),
      });
      let finalData = updated.data;
      if (editTotalAprobadoMode && editTotalAprobado > 0) {
        const reduced = await api.patch(`/presupuestos/${full.id}/reduccion`, {
          total_aprobado: parseFloat(editTotalAprobado),
        });
        finalData = reduced.data;
      }
      setFull(finalData);
      setEditOpen(false);
      toast.success("Presupuesto actualizado");
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSavingEdit(false);
    }
  }

  function openEditAseg() {
    setEditAsegForm({
      aseguradora_id: full.aseguradora_id ? String(full.aseguradora_id) : "",
      numero_poliza: full.numero_poliza ?? "",
      numero_siniestro: full.numero_siniestro ?? "",
      agente: full.agente ?? "",
      numero_provision: full.numero_provision ?? "",
    });
    setEditAsegOpen(true);
  }

  async function handleSaveAseg(e) {
    e.preventDefault();
    setSavingAseg(true);
    try {
      const updated = await api.patch(`/presupuestos/${full.id}/aseguradora`, {
        aseguradora_id: editAsegForm.aseguradora_id ? parseInt(editAsegForm.aseguradora_id) : null,
        numero_poliza: editAsegForm.numero_poliza || null,
        numero_siniestro: editAsegForm.numero_siniestro || null,
        agente: editAsegForm.agente || null,
        numero_provision: editAsegForm.numero_provision || null,
      });
      setFull(updated.data);
      setEditAsegOpen(false);
      toast.success("Datos de aseguradora actualizados");
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSavingAseg(false);
    }
  }

  function openEditRep(rep) {
    setEditRep(rep);
    setRepForm({
      proveedor_id: rep.proveedor_id ?? "",
      fecha_estimada_llegada: rep.fecha_estimada_llegada ?? "",
      notas: rep.notas ?? "",
    });
  }

  const hasInsurance = !!full.aseguradora_nombre;

  async function handleAprobar(e) {
    e.preventDefault();
    if (!fechaPago) { toast.error("Ingresá la fecha estimada de cobro"); return; }
    setSavingApproval(true);
    try {
      await api.patch(
        `/presupuestos/${full.id}/estado`,
        { fecha_estimada_pago: fechaPago || null },
        { params: { estado: "APROBADO" } },
      );
      toast.success("Presupuesto aprobado — cobro generado automáticamente");
      setApprovalDialog(false);
      setFull((d) => ({ ...d, estado: "APROBADO" }));
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al aprobar");
    } finally {
      setSavingApproval(false);
    }
  }

  async function saveRep(e) {
    e.preventDefault();
    setSavingRep(true);
    try {
      await api.put(`/presupuesto-repuestos/${editRep.id}`, {
        proveedor_id: repForm.proveedor_id ? parseInt(repForm.proveedor_id) : null,
        fecha_estimada_llegada: repForm.fecha_estimada_llegada || null,
        notas: repForm.notas || null,
      });
      toast.success("Repuesto actualizado");
      setEditRep(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally { setSavingRep(false); }
  }

  async function cambiarEstadoRep(id, estado, resp) {
    try {
      await api.patch(`/presupuesto-repuestos/${id}/estado`, null, {
        params: { estado, responsable: resp || undefined },
      });
      toast.success(`Estado → ${estado}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    }
  }

  function handlePrint() {
    api.get("/empresa").then((r) => printPresupuesto(full, r.data)).catch(() => printPresupuesto(full, {}));
  }

  function handlePrintHojaTrabajo() {
    api.get("/empresa").then((r) => printHojaTrabajo(full, r.data)).catch(() => printHojaTrabajo(full, {}));
  }

  const ESTADO_REP_BADGE = {
    PRESUPUESTADO: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300",
    DISPONIBLE:    "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300",
    RECHAZADO:     "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300",
    DADO_DE_BAJA:  "bg-gray-200 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 h-full w-full sm:w-[560px] bg-card border-l border-border flex flex-col shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card">
          <h2 className="font-semibold font-display">{full.numero}</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrint}
              className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400"
              title="Imprimir presupuesto"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={handlePrintHojaTrabajo}
              className="p-1.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 dark:text-amber-400"
              title="Imprimir hoja de trabajo"
            >
              <ClipboardList className="w-4 h-4" />
            </button>
            <button
              onClick={() => onImprimirConformidad(full.id)}
              className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400"
              title="Imprimir hoja de conformidad"
            >
              <ClipboardCheck className="w-4 h-4" />
            </button>
            {["BORRADOR", "ENVIADO"].includes(full.estado) && (
              <button
                onClick={openEditPresupuesto}
                className="p-1.5 rounded hover:bg-secondary text-muted-foreground"
                title="Editar presupuesto"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary text-muted-foreground">✕</button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {/* Info general */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Cliente: </span>{full.cliente_nombre}</div>
            <div><span className="text-muted-foreground">Vehículo: </span>{full.vehiculo_desc}</div>
            <div><span className="text-muted-foreground">Fecha: </span>{fmtDate(full.fecha)}</div>
            <div><span className="text-muted-foreground">Estado: </span>
              <Badge className={ESTADO_BADGE[full.estado]}>{full.estado}</Badge>
            </div>
            <div className="col-span-2 text-sm">
              <span className="text-muted-foreground">Aseguradora: </span>
              {full.aseguradora_nombre
                ? <span>{full.aseguradora_nombre}{full.numero_poliza ? ` · Póliza ${full.numero_poliza}` : ""}{full.numero_siniestro ? ` · Sin. ${full.numero_siniestro}` : ""}</span>
                : <span className="text-muted-foreground italic">Particular</span>}
            </div>
            {full.fecha_estimada_pago && (
              <div className="col-span-2"><span className="text-muted-foreground">Cobro estimado: </span><span className="font-medium">{fmtDate(full.fecha_estimada_pago)}</span></div>
            )}
          </div>

          {/* Tabla ítems */}
          <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[400px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-1.5 text-xs text-muted-foreground font-medium">Descripción</th>
                <th className="text-center py-1.5 text-xs text-muted-foreground font-medium">Tipo</th>
                <th className="text-right py-1.5 text-xs text-muted-foreground font-medium">Cant</th>
                <th className="text-right py-1.5 text-xs text-muted-foreground font-medium">P. Unit</th>
                <th className="text-right py-1.5 text-xs text-muted-foreground font-medium">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(full.items ?? []).map((it) => (
                <tr key={it.id} className="border-b border-border/50">
                  <td className="py-1.5">
                    {it.repuesto_codigo && <span className="font-mono text-xs text-muted-foreground mr-1">[{it.repuesto_codigo}]</span>}
                    {it.descripcion}
                  </td>
                  <td className="py-1.5 text-center text-xs text-muted-foreground">{it.tipo}</td>
                  <td className="py-1.5 text-right font-mono">{it.cantidad}</td>
                  <td className="py-1.5 text-right font-mono">{fmt(it.precio_unitario)}</td>
                  <td className="py-1.5 text-right font-mono">{fmt(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Totales */}
          <div className="text-sm space-y-1 text-right">
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono">{fmt(full.subtotal)}</span>
            </div>
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">Descuento ({full.descuento_pct}%)</span>
              <span className="font-mono">-{fmt(full.descuento_monto)}</span>
            </div>
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">IVA ({full.iva_pct}%)</span>
              <span className="font-mono">{fmt(full.iva_monto)}</span>
            </div>
            <div className="flex justify-end gap-8 font-bold text-base">
              <span>TOTAL</span>
              <span className="font-mono text-primary">{fmt(full.total)}</span>
            </div>
            {full.aprobado_reducido && (
              <div className="flex justify-end">
                <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700">
                  Presupuesto aprobado reducido
                </span>
              </div>
            )}
          </div>

          {/* Cambiar estado del presupuesto */}
          {full.estado !== "APROBADO" && full.estado !== "RECHAZADO" && (
            <div className="space-y-2 pt-2">
              <div className="flex gap-2">
                {full.estado === "BORRADOR" && (
                  <Button variant="outline" size="sm" onClick={() => onEstado(full.id, "ENVIADO")}>
                    Marcar enviado
                  </Button>
                )}
                <Button size="sm" onClick={() => { setFechaPago(""); setApprovalDialog(true); }}>
                  ✓ Aprobar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => onEstado(full.id, "RECHAZADO")}>
                  ✕ Rechazar
                </Button>
              </div>
            </div>
          )}

          {/* Dialog: aprobación — fecha de cobro */}
          <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} title="Aprobar presupuesto" size="sm">
            <form onSubmit={handleAprobar} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {hasInsurance
                  ? `¿Cuándo estimás que la aseguradora ${full.aseguradora_nombre} abonará este trabajo?`
                  : "¿Cuándo estimás cobrar este trabajo al cliente?"}
              </p>

              {/* Atajos rápidos */}
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Selección rápida</p>
                <div className="flex gap-2">
                  {[
                    { label: "7 días",  days: 7  },
                    { label: "15 días", days: 15 },
                    { label: "30 días", days: 30 },
                    { label: "60 días", days: 60 },
                  ].map(({ label, days }) => {
                    const d = new Date();
                    d.setDate(d.getDate() + days);
                    const iso = d.toISOString().slice(0, 10);
                    return (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setFechaPago(iso)}
                        className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                          fechaPago === iso
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:bg-secondary"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <FormField label="O ingresá una fecha exacta">
                <Input
                  type="date"
                  value={fechaPago}
                  onChange={(e) => setFechaPago(e.target.value)}
                  required
                />
              </FormField>

              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-300">
                Al aprobar se generará automáticamente un <strong>cobro pendiente</strong> en Facturación y una <strong>Orden de Trabajo</strong>.
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setApprovalDialog(false)}>Cancelar</Button>
                <Button type="submit" loading={savingApproval}>✓ Confirmar y aprobar</Button>
              </div>
            </form>
          </Dialog>

          {/* Sección Repuestos del presupuesto */}
          {(full.repuestos ?? []).length > 0 && (
            <div className="border-t border-border pt-4">
              <p className="text-sm font-semibold mb-3">Repuestos presupuestados</p>
              <div className="space-y-2">
                {(full.repuestos ?? []).map((rep) => (
                  <div key={rep.id} className="text-xs border border-border rounded-md p-3 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        {rep.codigo && <span className="font-mono text-muted-foreground mr-1">[{rep.codigo}]</span>}
                        <span className="font-medium">{rep.descripcion}</span>
                      </div>
                      <Badge className={ESTADO_REP_BADGE[rep.estado] ?? "bg-secondary"}>
                        {rep.estado}
                      </Badge>
                    </div>
                    <div className="text-muted-foreground">
                      Cant: {rep.cantidad} · P. Costo: {fmt(rep.precio_costo)}
                      {rep.proveedor_nombre && ` · ${rep.proveedor_nombre}`}
                      {rep.fecha_estimada_llegada && ` · Llegada: ${fmtDate(rep.fecha_estimada_llegada)}`}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {rep.estado === "PRESUPUESTADO" && (
                        <>
                          <button
                            onClick={() => cambiarEstadoRep(rep.id, "DISPONIBLE")}
                            className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            Disponible
                          </button>
                          <button
                            onClick={() => cambiarEstadoRep(rep.id, "RECHAZADO")}
                            className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            Rechazado
                          </button>
                        </>
                      )}
                      {rep.estado !== "DADO_DE_BAJA" && (
                        <button
                          onClick={() => { setBajaDialog(rep.id); setResponsable(""); }}
                          className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                          Dar de baja
                        </button>
                      )}
                      <button
                        onClick={() => openEditRep(rep)}
                        className="text-xs px-2 py-0.5 rounded bg-secondary hover:bg-border"
                      >
                        Editar
                      </button>
                    </div>
                    {rep.responsable_baja && (
                      <div className="text-muted-foreground">Baja: {rep.responsable_baja}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog editar repuesto */}
      <Dialog open={!!editRep} onClose={() => setEditRep(null)} title="Editar repuesto presupuestado" size="sm">
        <form onSubmit={saveRep} className="space-y-3">
          <FormField label="Proveedor">
            <Select value={repForm.proveedor_id} onChange={(e) => setRepForm((f) => ({ ...f, proveedor_id: e.target.value }))}>
              <option value="">Sin proveedor</option>
              {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </Select>
          </FormField>
          <FormField label="Fecha estimada llegada">
            <Input
              type="date"
              value={repForm.fecha_estimada_llegada}
              onChange={(e) => setRepForm((f) => ({ ...f, fecha_estimada_llegada: e.target.value }))}
            />
          </FormField>
          <FormField label="Notas">
            <Input
              value={repForm.notas}
              onChange={(e) => setRepForm((f) => ({ ...f, notas: e.target.value }))}
              placeholder="Observaciones…"
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setEditRep(null)}>Cancelar</Button>
            <Button type="submit" loading={savingRep}>Guardar</Button>
          </div>
        </form>
      </Dialog>

      {/* Dialog dar de baja */}
      <Dialog open={!!bajaDialog} onClose={() => setBajaDialog(null)} title="Dar de baja repuesto" size="sm">
        <div className="space-y-3">
          <FormField label="Responsable *">
            <Input
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              placeholder="Nombre del responsable…"
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBajaDialog(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!responsable.trim()) { toast.error("Ingresá el responsable"); return; }
                cambiarEstadoRep(bajaDialog, "DADO_DE_BAJA", responsable).then(() => setBajaDialog(null));
              }}
            >
              Confirmar baja
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Dialog editar presupuesto */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} title={`Editar presupuesto ${full.numero}`} size="2xl" className="!max-h-[96vh]">
        <form onSubmit={handleSaveEdit}>
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Panel izquierdo: ítems */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Fecha de emisión + botón editar aseguradora */}
              <div className="flex flex-wrap items-end gap-4">
                <FormField label="Fecha de emisión" className="flex-1 min-w-[140px]">
                  <Input
                    type="date"
                    value={editFecha}
                    onChange={(e) => setEditFecha(e.target.value)}
                  />
                </FormField>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openEditAseg}
                  className="mb-0.5"
                >
                  Editar aseguradora
                </Button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Ítems del presupuesto</span>
                  <Button type="button" variant="outline" size="sm" onClick={addEditItem}>
                    <Plus className="w-3.5 h-3.5" /> Agregar ítem
                  </Button>
                </div>
                <div className="overflow-x-auto">
                <div className="space-y-2 min-w-[480px]">
                  {editItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-end">
                      <FormField label={idx === 0 ? "Descripción" : undefined}>
                        <div className="flex gap-1">
                          {item.tipo === "REPUESTO" && (
                            <Input
                              value={item.codigo}
                              onChange={(e) => updateEditItem(idx, "codigo", e.target.value)}
                              placeholder="Código"
                              className="w-24"
                            />
                          )}
                          <Input
                            value={item.descripcion}
                            onChange={(e) => updateEditItem(idx, "descripcion", e.target.value)}
                            placeholder="Descripción…"
                            required
                          />
                        </div>
                      </FormField>
                      <FormField label={idx === 0 ? "Tipo" : undefined}>
                        <Select value={item.tipo} onChange={(e) => updateEditItem(idx, "tipo", e.target.value)} className="w-32">
                          {TIPOS_ITEM.map((t) => <option key={t}>{t}</option>)}
                        </Select>
                      </FormField>
                      <FormField label={idx === 0 ? "Cant." : undefined}>
                        <Input
                          type="number" min="0.01" step="0.01" className="w-20"
                          value={item.cantidad}
                          onChange={(e) => updateEditItem(idx, "cantidad", e.target.value)}
                        />
                      </FormField>
                      <FormField label={idx === 0 ? "P. Unit." : undefined}>
                        <Input
                          type="number" min="0" step="100" className="w-28"
                          value={item.precio_unitario}
                          onChange={(e) => updateEditItem(idx, "precio_unitario", e.target.value)}
                          onFocus={(e) => e.target.select()}
                        />
                      </FormField>
                      <div className={idx === 0 ? "pb-0 mt-5" : ""}>
                        <button
                          type="button"
                          onClick={() => removeEditItem(idx)}
                          disabled={editItems.length === 1}
                          className="p-2 text-muted-foreground hover:text-destructive disabled:opacity-30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </div>

              <FormField label="Observaciones">
                <Textarea rows={2} value={editObs} onChange={(e) => setEditObs(e.target.value)} />
              </FormField>
            </div>

            {/* Panel derecho: resumen */}
            <div className="w-full lg:w-64 shrink-0">
              <Card className="sticky top-0">
                <CardContent className="p-4 space-y-3">
                  <p className="font-semibold text-sm font-display">Resumen</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-mono">{fmt(editSubtotal)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs w-20">Descuento %</span>
                      <Input
                        type="number" min="0" max="100" step="0.5"
                        value={editDesc}
                        onChange={(e) => setEditDesc(parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs w-16"
                      />
                      <span className="font-mono ml-auto">-{fmt(editDescMonto)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs w-20">IVA %</span>
                      <Input
                        type="number" min="0" max="100"
                        value={editIva}
                        onChange={(e) => setEditIva(parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs w-16"
                      />
                      <span className="font-mono ml-auto">{fmt(editIvaMonto)}</span>
                    </div>
                  </div>
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between items-baseline">
                      <span className="font-semibold">TOTAL</span>
                      <span className="font-mono font-bold text-xl text-primary">{fmt(editTotal)}</span>
                    </div>
                  </div>

                  {/* Reducción de presupuesto aprobado */}
                  <div className="border-t border-border pt-3 space-y-2">
                    {!editTotalAprobadoMode ? (
                      <button
                        type="button"
                        onClick={() => { setEditTotalAprobadoMode(true); setEditTotalAprobado(editTotal); }}
                        className="w-full flex items-center justify-center gap-1.5 text-xs py-1.5 px-3 rounded-md border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 dark:border-orange-700 dark:text-orange-300 dark:bg-orange-950/30 dark:hover:bg-orange-900/40 transition-colors"
                      >
                        <ChevronsDown className="w-3.5 h-3.5" />
                        Reducción de presupuesto aprobado
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-orange-700 dark:text-orange-300">Total aprobado</span>
                          <button
                            type="button"
                            onClick={() => setEditTotalAprobadoMode(false)}
                            className="text-muted-foreground hover:text-foreground"
                            title="Cancelar reducción"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={editTotalAprobado}
                          onChange={(e) => setEditTotalAprobado(e.target.value)}
                          className="h-8 text-sm font-mono border-orange-300 dark:border-orange-700"
                        />
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          <div className="flex justify-between">
                            <span>IVA {editIva}% recalculado</span>
                            <span className="font-mono">{fmt(reducedIvaMonto)}</span>
                          </div>
                        </div>
                        <p className="text-[10px] text-orange-600 dark:text-orange-400 leading-snug">
                          Editar el total en caso de descuento o aprobación reducida por aseguradora.
                        </p>
                      </div>
                    )}
                  </div>

                  <Button type="submit" loading={savingEdit} className="w-full mt-2">
                    Guardar cambios
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Dialog>

      {/* Dialog editar aseguradora — se renderiza ÚLTIMO para quedar encima de editOpen */}
      <Dialog open={editAsegOpen} onClose={() => setEditAsegOpen(false)} title="Editar datos de aseguradora" size="sm">
        <form onSubmit={handleSaveAseg} className="space-y-3">
          <FormField label="Aseguradora">
            <Select
              value={editAsegForm.aseguradora_id}
              onChange={(e) => setEditAsegForm((f) => ({ ...f, aseguradora_id: e.target.value }))}
            >
              <option value="">Particular (sin aseguradora)</option>
              {aseguradoras.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </Select>
          </FormField>
          <FormField label="Nro. Póliza">
            <Input
              value={editAsegForm.numero_poliza}
              onChange={(e) => setEditAsegForm((f) => ({ ...f, numero_poliza: e.target.value }))}
              placeholder="Número de póliza…"
            />
          </FormField>
          <FormField label="Nro. Siniestro">
            <Input
              value={editAsegForm.numero_siniestro}
              onChange={(e) => setEditAsegForm((f) => ({ ...f, numero_siniestro: e.target.value }))}
              placeholder="Número de siniestro…"
            />
          </FormField>
          <FormField label="Agente">
            <Input
              value={editAsegForm.agente}
              onChange={(e) => setEditAsegForm((f) => ({ ...f, agente: e.target.value }))}
              placeholder="Nombre del agente…"
            />
          </FormField>
          <FormField label="Orden de provisión">
            <Input
              value={editAsegForm.numero_provision}
              onChange={(e) => setEditAsegForm((f) => ({ ...f, numero_provision: e.target.value }))}
              placeholder="Número de orden de provisión…"
            />
          </FormField>
          <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 rounded-md px-3 py-2">
            Estos cambios también actualizarán los datos de aseguradora en la recepción del vehículo.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setEditAsegOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={savingAseg}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

// ─── Función de impresión ─────────────────────────────────────────────────────
function guaraniesToWords(n) {
  n = Math.round(n);
  if (n === 0) return "CERO";
  const UNO = ["","UN","DOS","TRES","CUATRO","CINCO","SEIS","SIETE","OCHO","NUEVE",
    "DIEZ","ONCE","DOCE","TRECE","CATORCE","QUINCE","DIECISÉIS","DIECISIETE","DIECIOCHO","DIECINUEVE"];
  const DEC = ["","DIEZ","VEINTE","TREINTA","CUARENTA","CINCUENTA","SESENTA","SETENTA","OCHENTA","NOVENTA"];
  const CEN = ["","CIENTO","DOSCIENTOS","TRESCIENTOS","CUATROCIENTOS","QUINIENTOS",
    "SEISCIENTOS","SETECIENTOS","OCHOCIENTOS","NOVECIENTOS"];
  function cientos(x) {
    if (x === 0) return "";
    if (x === 100) return "CIEN";
    let s = "";
    if (x >= 100) { s += CEN[Math.floor(x / 100)] + " "; x %= 100; }
    if (x >= 20) { s += DEC[Math.floor(x / 10)]; if (x % 10) s += " Y " + UNO[x % 10]; }
    else if (x > 0) s += UNO[x];
    return s.trim();
  }
  const partes = [];
  const millones = Math.floor(n / 1e6); n %= 1e6;
  if (millones) partes.push(millones === 1 ? "UN MILLÓN" : cientos(millones) + " MILLONES");
  const miles = Math.floor(n / 1000); n %= 1000;
  if (miles) partes.push(miles === 1 ? "MIL" : cientos(miles) + " MIL");
  if (n > 0) partes.push(cientos(n));
  return partes.join(" ");
}

function printPresupuesto(pre, empresa) {
  const fmt2 = (n) => {
    if (n === null || n === undefined) return "0";
    return Math.round(n).toLocaleString("es-PY");
  };
  const fmtD = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const manoObra = (pre.items ?? []).filter((i) => i.tipo !== "REPUESTO");
  const repuestos = (pre.items ?? []).filter((i) => i.tipo === "REPUESTO");

  const manoObraTotal = manoObra.reduce((s, i) => s + (i.subtotal ?? 0), 0);
  const repuestosTotal = repuestos.reduce((s, i) => s + (i.subtotal ?? 0), 0);

  const nombreTaller = empresa.nombre || "VISION MOTORS";
  const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");
  const logoUrl = empresa.logo_url ? API_BASE + empresa.logo_url : null;

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Presupuesto ${pre.numero ?? ""}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; padding: 18px 22px; }

    /* ── HEADER ── */
    .header { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 2px solid #000; margin-bottom: 10px; }
    .header-logo img { height: 72px; width: auto; object-fit: contain; display: block; }
    .header-info { flex: 1; text-align: center; }
    .header-info h1 { font-size: 19px; font-weight: bold; letter-spacing: 1px; margin-bottom: 2px; }
    .header-info p { font-size: 9.5px; color: #333; line-height: 1.5; }
    .header-right { text-align: right; white-space: nowrap; }
    .doc-label { font-size: 15px; font-weight: bold; border: 2px solid #000; padding: 3px 10px; display: inline-block; letter-spacing: 1px; }
    .doc-num { font-size: 10px; margin-top: 5px; }

    /* ── INFO TABLE ── */
    .info-table { width: 100%; border-collapse: collapse; margin: 8px 0 12px 0; border: 1px solid #999; }
    .info-table td { padding: 4px 8px; border: 1px solid #ccc; font-size: 11px; vertical-align: middle; }
    .lbl { font-weight: bold; white-space: nowrap; }

    /* ── ITEMS ── */
    .section-title { font-weight: bold; font-size: 11px; text-align: center; text-decoration: underline; margin: 12px 0 4px 0; letter-spacing: 0.5px; }
    table.items { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
    table.items th { font-size: 10px; font-weight: bold; text-align: left; padding: 4px 6px;
      background: #f0f0f0; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; }
    table.items td { font-size: 11px; padding: 3px 6px; border-bottom: 1px solid #e5e5e5; vertical-align: top; }
    .tr { text-align: right; } .tc { text-align: center; }
    .total-row td { font-weight: bold; border-top: 1.5px solid #000; border-bottom: 2px solid #000; background: #f9f9f9; }

    /* ── SUMMARY ── */
    .obs { margin: 10px 0 6px 0; font-size: 10px; }
    .summary { margin-top: 14px; border: 1px solid #999; }
    .son-guaranies { padding: 5px 10px; border-bottom: 1px solid #ccc; font-size: 10px; }
    .totales { border-collapse: collapse; float: right; min-width: 260px; }
    .totales td { padding: 4px 10px; font-size: 11px; border-top: 1px solid #ccc; }
    .totales .lbl2 { text-align: right; color: #444; }
    .totales .val { font-family: monospace; text-align: right; font-weight: bold; }
    .totales .grand td { font-size: 13px; font-weight: bold; border-top: 2px solid #000; }
    .clearfix::after { content:""; display:table; clear:both; }

    /* ── FOOTER ── */
    .footer { margin-top: 24px; border-top: 1px solid #ccc; padding-top: 7px; font-size: 9px; color: #555; text-align: center; }

    /* ── MARCA DE AGUA ── */
    .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
      width: 55%; max-width: 420px; opacity: 0.11; pointer-events: none; z-index: 0; }

    @media print { body { padding: 10px; } }
  </style>
</head>
<body>

  <img class="watermark" src="${window.location.origin}/watermark.png" alt=""/>

  <!-- HEADER -->
  <div class="header">
    ${logoUrl ? `<div class="header-logo"><img src="${logoUrl}" alt="Logo"/></div>` : ""}
    <div class="header-info">
      <h1>${nombreTaller}</h1>
      ${empresa.label ? `<p>${empresa.label}</p>` : ""}
      ${empresa.direccion ? `<p>${empresa.direccion}</p>` : ""}
      ${(empresa.telefono || empresa.ruc) ? `<p>${empresa.telefono ? "Cel.: " + empresa.telefono : ""}${empresa.telefono && empresa.ruc ? "&nbsp;&nbsp;&nbsp;" : ""}${empresa.ruc ? "RUC: " + empresa.ruc : ""}</p>` : ""}
    </div>
    <div class="header-right">
      <div class="doc-label">PRESUPUESTO</div>
      <div class="doc-num">N°: <b>${pre.numero ?? ""}</b><br/>Fecha: ${fmtD(pre.fecha)}</div>
    </div>
  </div>

  <!-- INFO TABLE -->
  <table class="info-table">
    <tr>
      <td width="58%"><span class="lbl">Aseguradora:</span> ${pre.aseguradora_nombre ?? ""}</td>
      <td><span class="lbl">Fecha:</span> ${fmtD(pre.fecha)}</td>
    </tr>
    <tr>
      <td><span class="lbl">Propietario:</span> ${pre.cliente_nombre ?? ""}</td>
      <td><span class="lbl">Póliza Nro:</span> ${pre.numero_poliza ?? ""}</td>
    </tr>
    <tr>
      <td><span class="lbl">Marca:</span> ${pre.vehiculo_marca ?? ""}</td>
      <td><span class="lbl">Nro. Siniestro:</span> ${pre.numero_siniestro ?? ""}</td>
    </tr>
    <tr>
      <td><span class="lbl">Chasis:</span> ${pre.vehiculo_vin ?? ""}&nbsp;&nbsp;<span class="lbl">Modelo:</span> ${pre.vehiculo_modelo ?? ""}&nbsp;&nbsp;<span class="lbl">Color:</span> ${pre.vehiculo_color ?? ""}</td>
      <td><span class="lbl">Agente:</span> ${pre.agente ?? ""}&nbsp;&nbsp;&nbsp;<span class="lbl">Ord. Prov.:</span> ${pre.numero_provision ?? ""}</td>
    </tr>
    <tr>
      <td><span class="lbl">Teléfono:</span> ${pre.cliente_telefono ?? ""}&nbsp;&nbsp;&nbsp;<span class="lbl">Chapa:</span> ${pre.vehiculo_patente ?? ""}</td>
      <td><span class="lbl">Año:</span> ${pre.vehiculo_anio ?? ""}</td>
    </tr>
  </table>

  <!-- TRABAJOS Y MANO DE OBRA -->
  ${manoObra.length > 0 ? `
  <div class="section-title">— TRABAJOS Y MANO DE OBRA —</div>
  <table class="items">
    <thead><tr>
      <th>Descripción</th>
      <th class="tr" style="width:55px">Cant.</th>
      <th class="tr" style="width:120px">P. Unit.</th>
      <th class="tr" style="width:120px">Subtotal</th>
    </tr></thead>
    <tbody>
      ${manoObra.map((it) => `<tr>
        <td>${it.descripcion}</td>
        <td class="tr">${it.cantidad}</td>
        <td class="tr">₲ ${fmt2(it.precio_unitario)}</td>
        <td class="tr">₲ ${fmt2(it.subtotal)}</td>
      </tr>`).join("")}
      <tr class="total-row">
        <td colspan="3" class="tr">TOTAL TRABAJOS</td>
        <td class="tr">₲ ${fmt2(manoObraTotal)}</td>
      </tr>
    </tbody>
  </table>` : ""}

  <!-- REPUESTOS -->
  ${repuestos.length > 0 ? `
  <div class="section-title">— REPUESTOS —</div>
  <table class="items">
    <thead><tr>
      <th style="width:85px">Código</th>
      <th>Descripción</th>
      <th class="tr" style="width:55px">Cant.</th>
      <th class="tr" style="width:120px">P. Unit.</th>
      <th class="tr" style="width:120px">Subtotal</th>
    </tr></thead>
    <tbody>
      ${repuestos.map((it) => `<tr>
        <td style="font-family:monospace;font-size:10px">${it.repuesto_codigo ?? ""}</td>
        <td>${it.descripcion}</td>
        <td class="tr">${it.cantidad}</td>
        <td class="tr">₲ ${fmt2(it.precio_unitario)}</td>
        <td class="tr">₲ ${fmt2(it.subtotal)}</td>
      </tr>`).join("")}
      <tr class="total-row">
        <td colspan="4" class="tr">TOTAL REPUESTOS</td>
        <td class="tr">₲ ${fmt2(repuestosTotal)}</td>
      </tr>
    </tbody>
  </table>` : ""}

  <!-- OBSERVACIONES -->
  ${pre.observaciones ? `<p class="obs"><b>Observación:</b> ${pre.observaciones}</p>` : ""}

  <!-- SUMMARY BOX -->
  <div class="summary">
    <div class="son-guaranies">
      <b>Son Guaraníes:</b> ${guaraniesToWords(pre.total)} GUARANÍES
    </div>
    <div class="clearfix" style="padding:4px 0">
      <table class="totales">
        <tr><td class="lbl2">Sub Total:</td><td class="val">₲ ${fmt2(pre.subtotal)}</td></tr>
        ${pre.descuento_pct > 0 ? `<tr><td class="lbl2">Descuento (${pre.descuento_pct}%):</td><td class="val">- ₲ ${fmt2(pre.descuento_monto)}</td></tr>` : ""}
        <tr><td class="lbl2">IVA (${pre.iva_pct}%):</td><td class="val">₲ ${fmt2(pre.iva_monto)}</td></tr>
        <tr class="grand"><td class="lbl2">TOTAL</td><td class="val">₲ ${fmt2(pre.total)}</td></tr>
      </table>
    </div>
  </div>

  <!-- FOOTER -->
  <div class="footer">
    ${nombreTaller}${empresa.ruc ? " · RUC " + empresa.ruc : ""}${empresa.telefono ? " · " + empresa.telefono : ""}${empresa.direccion ? " · " + empresa.direccion : ""}
    <br/>Este presupuesto tiene validez de 30 días a partir de la fecha de emisión.
  </div>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) { toast.error("Popup bloqueado. Permitir popups e intentar de nuevo."); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
}

function printHojaTrabajo(pre, empresa) {
  const fmtD = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const manoObra = (pre.items ?? []).filter((i) => i.tipo !== "REPUESTO");
  const repuestos = (pre.items ?? []).filter((i) => i.tipo === "REPUESTO");

  const nombreTaller = empresa.nombre || "VISION MOTORS";
  const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");
  const logoUrl = empresa.logo_url ? API_BASE + empresa.logo_url : null;

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Hoja de Trabajo ${pre.numero ?? ""}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; padding: 18px 22px; }

    /* ── HEADER ── */
    .header { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 2px solid #000; margin-bottom: 10px; }
    .header-logo img { height: 72px; width: auto; object-fit: contain; display: block; }
    .header-info { flex: 1; text-align: center; }
    .header-info h1 { font-size: 19px; font-weight: bold; letter-spacing: 1px; margin-bottom: 2px; }
    .header-info p { font-size: 9.5px; color: #333; line-height: 1.5; }
    .header-right { text-align: right; white-space: nowrap; }
    .doc-label { font-size: 13px; font-weight: bold; border: 2px solid #000; padding: 3px 10px; display: inline-block; letter-spacing: 1px; }
    .doc-num { font-size: 10px; margin-top: 5px; }

    /* ── INFO TABLE ── */
    .info-table { width: 100%; border-collapse: collapse; margin: 8px 0 12px 0; border: 1px solid #999; }
    .info-table td { padding: 4px 8px; border: 1px solid #ccc; font-size: 11px; vertical-align: middle; }
    .lbl { font-weight: bold; white-space: nowrap; }

    /* ── ITEMS ── */
    .section-title { font-weight: bold; font-size: 11px; text-align: center; text-decoration: underline; margin: 12px 0 4px 0; letter-spacing: 0.5px; }
    table.items { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
    table.items th { font-size: 10px; font-weight: bold; text-align: left; padding: 4px 6px;
      background: #f0f0f0; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; }
    table.items td { font-size: 11px; padding: 4px 6px; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
    .tc { text-align: center; }
    .check-col { width: 60px; text-align: center; }
    .check-box { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #555; vertical-align: middle; }

    /* ── OBSERVACIONES ── */
    .obs-block { margin-top: 14px; border: 1px solid #ccc; padding: 8px 10px; min-height: 55px; }
    .obs-block-title { font-weight: bold; font-size: 10px; margin-bottom: 4px; }
    .obs-lines { border-bottom: 1px solid #ccc; margin-bottom: 8px; height: 14px; }

    /* ── FIRMAS ── */
    .firmas { display: flex; gap: 0; margin-top: 28px; border-top: 1px solid #ccc; padding-top: 16px; }
    .firma-col { flex: 1; text-align: center; padding: 0 12px; border-right: 1px solid #eee; }
    .firma-col:last-child { border-right: none; }
    .firma-line { border-top: 1px solid #555; margin: 0 auto 4px auto; width: 80%; margin-top: 36px; }
    .firma-label { font-size: 9.5px; color: #333; }

    /* ── FOOTER ── */
    .footer { margin-top: 20px; border-top: 1px solid #ccc; padding-top: 7px; font-size: 9px; color: #555; text-align: center; }

    /* ── MARCA DE AGUA ── */
    .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
      width: 55%; max-width: 420px; opacity: 0.11; pointer-events: none; z-index: 0; }

    @media print {
      body { padding: 10px; }
      @page { size: A4 portrait; margin: 12mm 15mm; }
    }
  </style>
</head>
<body>

  <img class="watermark" src="${window.location.origin}/watermark.png" alt=""/>

  <!-- HEADER -->
  <div class="header">
    ${logoUrl ? `<div class="header-logo"><img src="${logoUrl}" alt="Logo"/></div>` : ""}
    <div class="header-info">
      <h1>${nombreTaller}</h1>
      ${empresa.label ? `<p>${empresa.label}</p>` : ""}
      ${empresa.direccion ? `<p>${empresa.direccion}</p>` : ""}
      ${(empresa.telefono || empresa.ruc) ? `<p>${empresa.telefono ? "Cel.: " + empresa.telefono : ""}${empresa.telefono && empresa.ruc ? "&nbsp;&nbsp;&nbsp;" : ""}${empresa.ruc ? "RUC: " + empresa.ruc : ""}</p>` : ""}
    </div>
    <div class="header-right">
      <div class="doc-label">HOJA DE TRABAJO</div>
      <div class="doc-num">Presup.: <b>${pre.numero ?? ""}</b><br/>Fecha: ${fmtD(pre.fecha)}</div>
    </div>
  </div>

  <!-- INFO TABLE -->
  <table class="info-table">
    <tr>
      <td width="58%"><span class="lbl">Aseguradora:</span> ${pre.aseguradora_nombre ?? ""}</td>
      <td><span class="lbl">Fecha:</span> ${fmtD(pre.fecha)}</td>
    </tr>
    <tr>
      <td><span class="lbl">Propietario:</span> ${pre.cliente_nombre ?? ""}</td>
      <td><span class="lbl">Póliza Nro:</span> ${pre.numero_poliza ?? ""}</td>
    </tr>
    <tr>
      <td><span class="lbl">Marca / Modelo:</span> ${pre.vehiculo_marca ?? ""} ${pre.vehiculo_modelo ?? ""}</td>
      <td><span class="lbl">Nro. Siniestro:</span> ${pre.numero_siniestro ?? ""}</td>
    </tr>
    <tr>
      <td><span class="lbl">Chasis:</span> ${pre.vehiculo_vin ?? ""}&nbsp;&nbsp;<span class="lbl">Color:</span> ${pre.vehiculo_color ?? ""}</td>
      <td><span class="lbl">Agente:</span> ${pre.agente ?? ""}</td>
    </tr>
    <tr>
      <td><span class="lbl">Teléfono:</span> ${pre.cliente_telefono ?? ""}&nbsp;&nbsp;&nbsp;<span class="lbl">Chapa:</span> ${pre.vehiculo_patente ?? ""}</td>
      <td><span class="lbl">Año:</span> ${pre.vehiculo_anio ?? ""}</td>
    </tr>
  </table>

  <!-- TRABAJOS Y MANO DE OBRA -->
  ${manoObra.length > 0 ? `
  <div class="section-title">— TRABAJOS Y MANO DE OBRA —</div>
  <table class="items">
    <thead><tr>
      <th>Descripción</th>
      <th class="tc" style="width:55px">Cant.</th>
      <th class="tc check-col">Realizado</th>
    </tr></thead>
    <tbody>
      ${manoObra.map((it) => `<tr>
        <td>${it.descripcion}</td>
        <td class="tc">${it.cantidad}</td>
        <td class="tc"><span class="check-box"></span></td>
      </tr>`).join("")}
    </tbody>
  </table>` : ""}

  <!-- REPUESTOS -->
  ${repuestos.length > 0 ? `
  <div class="section-title">— REPUESTOS —</div>
  <table class="items">
    <thead><tr>
      <th style="width:85px">Código</th>
      <th>Descripción</th>
      <th class="tc" style="width:55px">Cant.</th>
      <th class="tc check-col">Instalado</th>
    </tr></thead>
    <tbody>
      ${repuestos.map((it) => `<tr>
        <td style="font-family:monospace;font-size:10px">${it.repuesto_codigo ?? ""}</td>
        <td>${it.descripcion}</td>
        <td class="tc">${it.cantidad}</td>
        <td class="tc"><span class="check-box"></span></td>
      </tr>`).join("")}
    </tbody>
  </table>` : ""}

  <!-- OBSERVACIONES -->
  <div class="obs-block">
    <div class="obs-block-title">Observaciones / Notas del técnico:</div>
    ${pre.observaciones ? `<p style="font-size:10px;margin-bottom:6px">${pre.observaciones}</p>` : ""}
    <div class="obs-lines"></div>
    <div class="obs-lines"></div>
    <div class="obs-lines"></div>
  </div>

  <!-- FIRMAS -->
  <div class="firmas">
    <div class="firma-col">
      <div class="firma-line"></div>
      <div class="firma-label">Jefe de Taller</div>
    </div>
    <div class="firma-col">
      <div class="firma-line"></div>
      <div class="firma-label">Técnico Responsable</div>
    </div>
    <div class="firma-col">
      <div class="firma-line"></div>
      <div class="firma-label">Control de Calidad</div>
    </div>
  </div>

  <!-- FOOTER -->
  <div class="footer">
    ${nombreTaller}${empresa.ruc ? " · RUC " + empresa.ruc : ""}${empresa.telefono ? " · " + empresa.telefono : ""}${empresa.direccion ? " · " + empresa.direccion : ""}
    <br/>Documento interno — USO EXCLUSIVO DEL TALLER
  </div>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) { toast.error("Popup bloqueado. Permitir popups e intentar de nuevo."); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
}

